import { Help } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { get, set } from 'lodash';
import React, { FC, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FORM_FIELDS_TYPES } from '../../../../../lib/constants/constant';
import { MUIFieldProps } from '../../../../../lib/types/types';
import { DisplayDate, DisplayDecimal, DisplayEuro } from '../../../../../utils/formats';
import { mapTablePath } from '../../../../../utils/formStruc';
import { enumT, fieldNameTranslation, isTrue } from '../../../../../utils/helper';
import MUIToolTip from '../../../mui-tooltip/MUIToolTip';
import FormFields from '../FormFields';
import SingleUpload from './SingleUpload';

const HierarchicalEditableTable: FC<MUIFieldProps> = (props) => {
	const { fieldValue, updateFieldValue, isDisabled } = props;

	const component = get(props, 'component');
	const componentPart = component.split('.');
	const apiName = useMemo(() => {
		if (get(props, 'api')) {
			return get(props, 'api');
		} else {
			return componentPart[0] + 's';
		}
	}, [componentPart, props]);

	const columns = get(props, 'schema.attributes');

	// Konfigurierbare Props
	const rowTypeField = get(props, 'hierarchicalProps.rowTypeField'); // Feldname für Typ (z.B. 'Zelle_6')
	const groupIdField = get(props, 'hierarchicalProps.groupIdField', 'Group_id'); // Feldname für Gruppierung (z.B. 'Group_id')

	// Enum-Werte DYNAMISCH aus dem Schema holen
	const enumValues = get(columns, `${rowTypeField}.enum`, []);
	const parentType = enumValues[0]; // Erster Enum-Wert = Parent (z.B. "Meilenstein")
	const childType = enumValues[1]; // Zweiter Enum-Wert = Child (z.B. "Aufgabe")

	const hideColumnsForParent = get(props, 'hierarchicalProps.hideColumnsForParent', []); // Spalten die bei Parent ausgeblendet werden

	const isFieldDisabled = (columnKey: string, row: any) => {
		if (isDisabled) return true;
		if (get(props, 'disabledColumns', []).includes(columnKey)) {
			return true;
		}
		// Spalten bei Parent-Zeilen ausblenden
		const isParent = row[rowTypeField] === parentType;
		if (isParent && hideColumnsForParent.includes(columnKey)) {
			return true;
		}
		return false;
	};

	const tablePath = get(props, 'path', '');
	const width = get(props, 'width', '100%');

	// Parent hinzufügen (Hauptbutton)
	const handleAddParent = () => {
		const newGroupId = uuidv4().replace(/-/g, '').slice(0, 12); // 12-stellige Group_id ohne Bindestriche
		const newParent: any = {};

		// Alle Spalten mit null initialisieren
		Object.keys(columns).forEach((key) => {
			if (key === rowTypeField) {
				newParent[key] = parentType; // Typ-Feld mit parentType setzen
			} else if (key === groupIdField) {
				newParent[key] = newGroupId; // Group_id setzen
			} else if (hideColumnsForParent.includes(key)) {
				newParent[key] = null;
			} else {
				newParent[key] = null;
			}
		});

		const newValue = [...(fieldValue || []), newParent];
		let e = { target: { value: newValue } };
		updateFieldValue(e as unknown as React.ChangeEvent<HTMLInputElement>);
	};

	// Child zu einem Parent hinzufügen
	const handleAddChild = (parentGroupId: string) => {
		const newChild: any = {};

		// Alle Spalten mit null initialisieren
		Object.keys(columns).forEach((key) => {
			if (key === rowTypeField) {
				newChild[key] = childType; // Typ-Feld mit childType setzen
			} else if (key === groupIdField) {
				newChild[key] = parentGroupId; // Gleiche Group_id wie der Parent
			} else {
				newChild[key] = null;
			}
		});

		// Reihenfolge der zugrundeliegenden Daten ist egal: ans Ende anhängen
		const newValue = [...(fieldValue || []), newChild];
		let e = { target: { value: newValue } };
		updateFieldValue(e as unknown as React.ChangeEvent<HTMLInputElement>);
	};

	const handleChange = (path: any, value: any, rowIndex: number) => {
		// Prüfe ob die Spalte Teil einer Radiogruppe ist
		const radioGroups = get(props, 'tableProps.radioGroups', []);
		if (radioGroups.includes(path) && value === true) {
			// Setze alle anderen Radiogruppen-Spalten in dieser Zeile auf false
			radioGroups.forEach((group: string) => {
				if (group !== path) {
					set(fieldValue, `${rowIndex}.${group}`, false);
				}
			});
		}

		set(fieldValue, `${rowIndex}.${path}`, value);

		let e = { target: { value: fieldValue } };
		updateFieldValue(e as React.ChangeEvent<HTMLInputElement>);
	};

	const handleDelete = (rowIndex: number) => {
		const rowToDelete = fieldValue[rowIndex];
		const groupId = rowToDelete[groupIdField];
		let newRow;

		// Wenn Parent gelöscht wird, auch alle zugehörigen Children löschen
		if (rowToDelete[rowTypeField] === parentType) {
			// Lösche alle Zeilen mit der gleichen Group_id
			newRow = fieldValue.filter((row: any) => row[groupIdField] !== groupId);
		} else {
			// Nur das Child löschen
			newRow = fieldValue.filter((_: any, index: number) => index !== rowIndex);
		}

		let e = { target: { value: newRow } };
		updateFieldValue(e as React.ChangeEvent<HTMLInputElement>);
	};

	const visibleColumns = Object.keys(columns).filter((key: string) => !get(props, 'hideColumns', []).includes(key));

	const showColGroups = isTrue(get(props, 'colGroups'));

	const startingKey = get(props, 'colGroupsProps.startingKey');
	const colSpan = get(props, 'colGroupsProps.span', 1);
	const label = get(props, 'colGroupsProps.label', '');
	const spannedColTooltip = get(props, 'colGroupsProps.tooltip', '');

	let skip = 0;

	// Anzeige-Reihen (unabhängig von der DB-Reihenfolge) nach Group_id gruppieren
	const displayRows = useMemo(() => {
		const rows: any[] = fieldValue || [];
		const byGroup: Record<string, { parent?: { row: any; index: number }; children: Array<{ row: any; index: number }> }> = {};
		const order: string[] = [];

		rows.forEach((row, index) => {
			const gid = row[groupIdField];
			if (!gid) return;
			if (!byGroup[gid]) {
				byGroup[gid] = { children: [] };
				order.push(gid);
			}
			if (row[rowTypeField] === parentType) {
				byGroup[gid].parent = { row, index };
			} else if (row[rowTypeField] === childType) {
				byGroup[gid].children.push({ row, index });
			}
		});

		const result: Array<{ row: any; index: number }> = [];
		order.forEach((gid) => {
			const group = byGroup[gid];
			if (group.parent) result.push(group.parent);
			result.push(...group.children);
		});

		return result;
	}, [fieldValue, groupIdField, rowTypeField, parentType, childType]);
	return (
		<>
			<Box sx={{ mt: '1rem' }}>
				{get(props, 'tableLabel', '') && (
					<Typography sx={{ mb: 1, fontWeight: 600 }}>
						{get(props, 'tableLabel', '')}{' '}
						{get(props, 'tableLabelTooltip', '') ?
							<MUIToolTip title={get(props, 'tableLabelTooltip', '')}>
								<Help fontSize='small' color='primary' sx={{ ml: '5px' }} />{' '}
							</MUIToolTip>
						:	<></>}
					</Typography>
				)}
				<TableContainer component={'div'} sx={{ width: width }}>
					<Table className='editable-table'>
						<TableHead>
							{showColGroups && (
								<TableRow>
									{/* eslint-disable-next-line array-callback-return */}
									{visibleColumns.map((eachCol: string, i: number) => {
										if (skip > 0) {
											skip--;
											return null;
										}
										if (eachCol === startingKey) {
											skip = colSpan - 1;
											return (
												<TableCell key={startingKey + i} colSpan={colSpan}>
													{label}
													{spannedColTooltip && (
														<MUIToolTip title={spannedColTooltip}>
															<Help fontSize='small' color='primary' sx={{ ml: '5px' }} />
														</MUIToolTip>
													)}
												</TableCell>
											);
										} else {
											return <TableCell key={startingKey + i} rowSpan={1} />;
										}
									})}
								</TableRow>
							)}
							<TableRow>
								{visibleColumns.map((key) => {
									const colDef = columns[key];
									return (
										<TableCell
											key={key}
											align={['integer', 'decimal', 'date'].includes(get(colDef, 'type')) ? 'right' : 'left'}
											sx={{
												padding: '0.5rem',
												width: get(props, `fieldProps.${key}.width`, 'auto'),
												maxWidth: get(props, `fieldProps.${key}.maxWidth`, '0'),
												writingMode: get(props, `fieldProps.${key}.wirtingMode`, 'initial'),
											}}
										>
											<>
												{getColumnLabel(get(colDef, 'path'), key, get(props, 'fieldProps'), apiName)}
												{get(props, `fieldProps.${key}.tooltip`, '') && (
													<MUIToolTip title={get(props, `fieldProps.${key}.tooltip`, '')}>
														<Help fontSize='small' color='primary' sx={{ ml: '5px' }} />
													</MUIToolTip>
												)}
											</>
										</TableCell>
									);
								})}
								{!get(props, 'hideDeleteButton') && !isDisabled && (
									<TableCell sx={{ padding: '0.5rem 1rem', backgroundColor: 'background.paper' }} className='pinned-right-column'>
										{!get(props, 'hideAddButton') && (
											<IconButton
												onClick={() => handleAddParent()}
												disabled={isDisabled || (fieldValue || []).length >= get(props, 'maxLevel', 999)}
												title={get(props, 'hierarchicalProps.addParentLabel', 'Parent hinzufügen')}
											>
												<AddCircleOutlineIcon />
											</IconButton>
										)}
									</TableCell>
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{displayRows.map(({ row: eachRow, index: originalIndex }) => {
								const isParent = eachRow[rowTypeField] === parentType;
								const isChild = eachRow[rowTypeField] === childType;

								return (
									<TableRow
										key={(eachRow[groupIdField] ? eachRow[groupIdField] : 'row') + '-' + originalIndex}
										sx={{ backgroundColor: isParent ? '#f5f5f5' : 'inherit' }}
									>
										{visibleColumns.map((columnKey) => {
											const colDef = columns[columnKey];
											const attributePathWithIndex = mapTablePath(colDef.path, originalIndex, tablePath);
											const hideColumnForParent = isParent && hideColumnsForParent.includes(columnKey);

											return (
												<TableCell
													key={columnKey}
													sx={{
														padding: '0.5rem',
														maxWidth: 0,
														minWidth: get(props, `fieldProps.${columnKey}.width`, 'auto'),
													}}
													align={
														isFieldDisabled(columnKey, eachRow) && ['integer', 'decimal', 'date'].includes(get(colDef, 'type')) ?
															'right'
														:	'left'
													}
												>
													{hideColumnForParent ?
														<Typography sx={{ color: 'text.disabled' }}>—</Typography>
													: isFieldDisabled(columnKey, eachRow) ?
														<Typography>
															{getDisplayText(
																colDef.type,
																eachRow[columnKey],
																get(props, `fieldProps.${columnKey}.format`, ''),
																get(props, `fieldProps.${columnKey}.customLabel`, ''),
																get(props, `fieldProps.${columnKey}.pathForLabel`, ''),
															)}
														</Typography>
													:	<FormFields
															{...colDef}
															label=''
															type={get(props, `fieldProps.${columnKey}.type`, colDef.type)}
															formValues={eachRow}
															formErrors={get(props, 'formErrors')}
															format={get(props, `fieldProps.${columnKey}.format`)}
															path={columnKey}
															onChange={(path, value) => {
																handleChange(path, value, originalIndex);
															}}
															disabled={isDisabled || get(props, 'disabledColumns', []).includes(columnKey)}
															editableTablePathWIthIndex={attributePathWithIndex}
															allowOnlyImage={get(props, `fieldProps.${columnKey}.allowOnlyImage`)}
															size='small'
															options={get(props, `fieldProps.${columnKey}.options`, colDef.options)}
															multiple={get(props, `fieldProps.${columnKey}.multiple`)}
															stringMode={get(props, `fieldProps.${columnKey}.stringMode`)}
															separator={get(props, `fieldProps.${columnKey}.separator`)}
															maxCharLength={get(props, `fieldProps.${columnKey}.maxCharLength`)}
														/>
													}
												</TableCell>
											);
										})}

										{!get(props, 'hideDeleteButton') && !isDisabled && (
											<TableCell
												className='pinned-right-column'
												sx={{ padding: isChild ? '0.5rem 1rem' : '0.1rem', backgroundColor: 'background.paper' }}
											>
												<Stack direction='row' spacing={0}>
													{isParent && (
														<IconButton
															onClick={() => handleAddChild(eachRow[groupIdField])}
															disabled={isDisabled}
															title={get(props, 'hierarchicalProps.addChildLabel', 'Child hinzufügen')}
															size='small'
														>
															<AddCircleOutlineIcon fontSize='small' />
														</IconButton>
													)}
													<IconButton
														onClick={() => handleDelete(originalIndex)}
														disabled={isDisabled}
														title={
															isChild ?
																get(props, 'hierarchicalProps.deleteChild', 'Child Löschen')
															:	get(props, 'hierarchicalProps.deleteParent', 'Parent Löschen')
														}
													>
														<DeleteIcon />
													</IconButton>
												</Stack>
											</TableCell>
										)}
									</TableRow>
								);
							})}
							{get(props, 'sumRow', []).map((lastRow: any, index: number) => {
								return (
									<TableRow key={`sum-${index}`} sx={{ backgroundColor: 'background.paper' }}>
										{visibleColumns.map((columnKey) => {
											const colDef = columns[columnKey];
											return (
												<TableCell
													key={columnKey}
													sx={{ padding: '0.5rem 1rem' }}
													align={['integer', 'decimal', 'date'].includes(get(colDef, 'type')) ? 'right' : 'left'}
												>
													<Typography>
														{getDisplayText(colDef.type, lastRow[columnKey], get(props, `fieldProps.${columnKey}.format`))}
													</Typography>
												</TableCell>
											);
										})}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</>
	);
};
export default HierarchicalEditableTable;

function getColumnLabel(path: string, key: string, fieldProps: any, apiName: string): string {
	if (fieldProps && fieldProps[key] && fieldProps[key].label) {
		return fieldProps[key].label;
	}

	const parts: string[] = path.split('.');
	const lastPart: string = parts.pop()!;

	if (lastPart.startsWith('Zelle')) {
		const zelleNumber: string = lastPart.match(/\d+/)![0];
		let blockPart: string = parts.pop()!;
		blockPart = blockPart.includes('Block_') ? blockPart : blockPart;
		const modifiedBlockPart: string = blockPart + `_Spalte_${zelleNumber}`;
		return fieldNameTranslation(modifiedBlockPart, apiName);
	} else {
		return fieldNameTranslation(lastPart, apiName);
	}
}

function getDisplayText(type: string, value: any, format?: string, label?: string, keyForlabel?: string) {
	if (value === undefined || value === null) return '';
	if (label) return label;

	switch (type) {
		case 'decimal':
			if (format === 'currency') {
				return DisplayEuro(value);
			}
			if (format === 'percentage') {
				return DisplayDecimal(value, true);
			}
			return DisplayDecimal(value);
		case 'date':
			return DisplayDate(value);
		case 'enumeration':
			return enumT(value);
		case 'boolean':
			return isTrue(value) ? 'Ja' : 'Nein';
		case 'relation':
			return get(value, keyForlabel, '');
		case FORM_FIELDS_TYPES.media:
			return <SingleUpload label={''} fieldValue={value} updateFieldValue={(_e) => {}} isDisabled={true} error={false} errorMessage={''} />;
		default:
			return value;
	}
}
